import { Component, Host, Prop, h, Element, State } from '@stencil/core';
import { getDataFromPropString } from '../../utils/utils';
import { convertToBoolean, handleScrollAndFocus } from '../vs-featured/featured-component-utils';

@Component({
  tag: 'vs-ambient-video',
  styleUrl: 'vs-ambient-video.scss',
  shadow: false,
})
export class VsAmbientVideo {
  @Element() element: HTMLVsAmbientVideoElement;
  private videoData: any = {};
  private video: HTMLVideoElement;
  private keyboardFocus: boolean = false;

  /**
   * The data string containing video information.
   */
  @Prop() data: string;
  /**
   * The dimension of the image.
   */
  @Prop() imageDimension = 'vs-1-1';
  /**
   * Enable or disable rounded corners for the video.
   */
  @Prop() enableRoundedCorners?: string = 'true';

  @State() isVideoPlaying: boolean = true;

  /**
    Tabindex for the video component.
  **/
  @Prop() tIndex?: any = '';

  /**
   * The root element for handling scroll and focus behavior.
   */
  @Prop() rootElement?: any;

  componentWillLoad() {
    this.videoData = getDataFromPropString(this.data);
  }

  componentDidLoad() {
    this.video = this.element.querySelector('video');
    this.element.addEventListener('keydown', this.handleKeyDown);
  }

  disconnectedCallback() {
    this.element.removeEventListener('keydown', this.handleKeyDown);
  }

  private onPlayPauseClick = () => {
    if (this.video) {
      this.isVideoPlaying ? this.video.pause() : this.video.play();
      this.isVideoPlaying = !this.isVideoPlaying;
    }
  };

  private getAriaLabel(): string {
    const actionLabel = this.videoData?.accessibility?.[
      this.isVideoPlaying ? 'pauseButtonAriaLabel' : 'playButtonAriaLabel'
    ] || (this.isVideoPlaying ? 'Pause' : 'Play');
    const headerText = this.videoData?.headerText || '';
    return `${actionLabel} ${headerText}`.trim();
  }

  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Tab') {
      this.keyboardFocus = true;
    }
  };

  private handleFocusIn = () => {
    if (this.keyboardFocus) {
      const currentElem: any = this.element.querySelector('.vs-amb-video-btn');
      if (currentElem && this.rootElement) {
        const SCROLL_TIMEOUT = 100;
        setTimeout(() => {
          handleScrollAndFocus(currentElem, this.rootElement);
        }, SCROLL_TIMEOUT);
      }
    }
    this.keyboardFocus = false;
  };

  render() {
    return (
      <Host>
        <div
          class="vs-amb-video-container"
        >
          <video
            class={`vs-amb-video ${convertToBoolean(this?.enableRoundedCorners) ? 'vs-rounded-image' : ''} vs-featured-image ${this.imageDimension}`}
            muted
            autoplay
            loop
            playsinline
            poster={this.videoData.poster}
            aria-label={this.videoData.videoAriaLabel}
          >
            <source src={this.videoData.videoUrl} type="video/mp4" />
          </video>
          <div
            role='switch'
            aria-checked={this.isVideoPlaying.toString()}
            tabIndex={this.tIndex}
            aria-label={this.getAriaLabel()}
            class={`vs-amb-video-btn vs-amb-video-${this.isVideoPlaying ? 'pause' : 'play'}-btn vs-amb-video-btn-${this.videoData.videoMediaControlBtn}`}
            onClick={this.onPlayPauseClick}
            onFocusin={this.handleFocusIn}
          >
            <span class="vs-sr-only">{this.getAriaLabel()}</span>
          </div>
        </div>
      </Host>
    );
  }
}
